import {
  useConsumerAnalysis,
  useConsumersList,
} from "@features/bx/BulkBillGeneration/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { Switch } from "@shared/ui/switch";
import { Badge } from "@shared/ui/badge";
import { PAGE_SIZE } from "@shared/utils/constants";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Users,
  User,
  Home,
  Building2,
  UserCircle,
  UserCheck,
  UserX,
  UserMinus,
  FileText,
  Repeat,
  Banknote,
  Wrench,
} from "lucide-react";
import React, { useState } from "react";
import { useParams } from "react-router-dom";

const SectionHeader = ({
  title,
  description,
}: {
  title: string;
  description?: string;
}) => (
  <div className={description ? "mb-2" : "mb-4"}>
    <h3 className="text-lg font-semibold">{title}</h3>
    {description && <p className="text-sm text-gray-600">{description}</p>}
  </div>
);

interface ConsumerTabProps {
  data: any;
  includeFirstBillCharges: boolean;
  setIncludeFirstBillCharges: (value: boolean) => void;
}

const ConsumerTab: React.FC<ConsumerTabProps> = ({
  data,
  includeFirstBillCharges,
  setIncludeFirstBillCharges,
}) => {
  const { id, cycleId, action } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [showConsumerList, setShowConsumerList] = useState(false);
  const { data: consumerDashboardData } = useConsumerAnalysis(cycleId);
  const { data: consumerList } = useConsumersList(
    cycleId,
    currentPage,
    PAGE_SIZE,
  );

  const analysisCards = [
    {
      title: "Total",
      count: consumerDashboardData?.overview?.totalConsumers,
      icon: <Users className="w-6 h-6 text-blue-500" />,
      // percentage: "100%",
      borderColor: "border-l-blue-500",
    },
    {
      title: "Active",
      count: consumerDashboardData?.overview?.activeConsumers,
      icon: <UserCheck className="w-6 h-6 text-green-500" />,
      // percentage: "99.9%",
      borderColor: "border-l-green-500",
    },
    {
      title: "Temporary Disconnected",
      count: consumerDashboardData?.overview?.temporaryDisconnected,
      icon: <UserMinus className="w-6 h-6 text-orange-500" />,
      // percentage: "0.03%",
      borderColor: "border-l-orange-500",
    },
    {
      title: "Inactive",
      count: consumerDashboardData?.overview?.inactiveConsumers,
      icon: <UserX className="w-6 h-6 text-red-500" />,
      // percentage: "0%",
      borderColor: "border-l-red-500",
    },
  ];

  const getStatusBadgeConfig = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return {
          variant: "default" as const,
          className: "bg-green-100 text-green-800",
        };
      case "TEMPORARY DISCONNECTED":
        return {
          variant: "secondary" as const,
          className: "bg-gray-100 text-gray-800",
        };
      default:
        return {
          variant: "outline" as const,
          className: "bg-gray-100 text-gray-800",
        };
    }
  };

  // Define columns for the table
  const columns: TableColumn<any>[] = [
    {
      key: "accountNo",
      header: "Account No",
      type: "text",
    },
    {
      key: "consumerName",
      header: "Consumer Name",
      type: "text",
    },
    {
      key: "utilityService",
      header: "Utility Service",
      width: "w-64",
      render: (services) => (
        <div className="flex gap-2 flex-wrap">
          {Array.isArray(services) && services.length > 0 ? (
            services.map((service, idx) => (
              <Badge key={idx} variant="secondary">
                {service}
              </Badge>
            ))
          ) : (
            <span className="text-sm text-gray-700">NA</span>
          )}
        </div>
      ),
    },
    {
      key: "categoryDisplay",
      header: "Category",
      render: (category) =>
        category ? (
          <Badge variant="outline">{category}</Badge>
        ) : (
          <span className="text-sm text-gray-700">NA</span>
        ),
    },
    {
      key: "premiseDisplay",
      header: "Premise",
      type: "text",
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => getStatusBadgeConfig(value).variant,
        getClassName: (value: string) => getStatusBadgeConfig(value).className,
      },
    },
  ];

  const firstBillMetrics = [
    {
      label: "Total Consumers with First Bills",
      value:
        consumerDashboardData?.newConsumerFirstBillOverview
          ?.totalNewConsumersFirstBill,
      subtext: "New consumers in this cycle",
      icon: <FileText className="w-6 h-6 text-blue-500" />,
    },
    {
      label: "Consumers with Charges Carry Forward",
      value:
        consumerDashboardData?.newConsumerFirstBillOverview
          ?.consumersWithChargesCarryForward,
      subtext: "Previous balance carried over",
      icon: <Repeat className="w-6 h-6 text-purple-500" />,
    },
    {
      label: "Total Deposit",
      value: consumerDashboardData?.newConsumerFirstBillOverview?.totalDeposit,
      subtext: "Security deposits collected",
      icon: <Banknote className="w-6 h-6 text-green-500" />,
    },
    {
      label: "Total Installation Charges",
      value:
        consumerDashboardData?.newConsumerFirstBillOverview
          ?.totalInstallationCharges,
      subtext: "One-time setup fees",
      icon: <Wrench className="w-6 h-6 text-orange-500" />,
    },
  ];

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const consumerStats = [
    {
      label: "Previous Cycle Consumers",
      value: consumerDashboardData?.cycleComparison?.previousCycleConsumers,
      bg: "bg-purple-50",
      border: "border-purple-200",
      text: "text-purple-700",
      icon: <Users className="w-6 h-6 text-purple-500" />,
    },
    {
      label: "Current Cycle Consumers",
      value: consumerDashboardData?.cycleComparison?.currentCycleConsumers,
      bg: "bg-green-50",
      border: "border-green-200",
      text: "text-green-700",
      icon: <Users className="w-6 h-6 text-green-500" />,
    },
    {
      label: "New Consumers",
      value: consumerDashboardData?.cycleComparison?.newConsumers,
      bg: "bg-blue-50",
      border: "border-blue-200",
      text: "text-blue-700",
      icon: <UserCheck className="w-6 h-6 text-blue-500" />,
    },
  ];

  const accountDist = consumerDashboardData?.accountTypeDistribution || {};

  const accountTypeMetrics = [
    {
      title: "Total Active Accounts",
      count: accountDist.totalActiveAccounts ?? "NA",
      displayValue: accountDist.totalActiveAccounts ?? "NA",
      trend: 100,
      trendDurationDisplay: "of total",
      icon: <Users className="w-6 h-6 text-blue-500" />,
    },
    {
      title: "Individual",
      count: accountDist.individual ?? "NA",
      displayValue: accountDist.individual ?? "NA",
      trend: accountDist.individualPct ?? "NA",
      trendDurationDisplay: "of total",
      icon: <User className="w-6 h-6 text-green-500" />,
    },
    {
      title: "Tenant",
      count: accountDist.tenant || "NA",
      displayValue: accountDist.tenant || "NA",
      trend: accountDist.tenantPct || "NA",
      trendDurationDisplay: "of total",
      icon: <Home className="w-6 h-6 text-orange-500" />,
    },
    {
      title: "Master",
      count: accountDist.master || "NA",
      displayValue: accountDist.master || "NA",
      trend: accountDist.masterPct || "NA",
      trendDurationDisplay: "of total",
      icon: <Building2 className="w-6 h-6 text-purple-500" />,
    },
    {
      title: "Sub-Account",
      count: accountDist.subAccount || 0,
      displayValue: (accountDist.subAccount || 0).toLocaleString(),
      trend: accountDist.subAccountPct || 0,
      trendDurationDisplay: "of total",
      icon: <UserCircle className="w-6 h-6 text-teal-500" />,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Consumer Data Analysis */}
      <div className="mb-6">
        <SectionHeader
          title="Consumer Data Analysis"
          description="Overview of consumer status and distribution for the selected cycle."
        />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {analysisCards.map((card, index) => (
            <QuickStatsCard
              key={index}
              title={card.title}
              count={card.count as number}
              icon={card.icon}
            />
          ))}
        </div>
      </div>

      {/* New Consumers First Bill Overview */}
      <div className="mb-6">
        <SectionHeader
          title="New Consumers First Bill Overview"
          description="Installation charges and deposits will be included in the first bill for new consumers. These one-time fees are automatically calculated and added to the initial billing cycle."
        />
        <div className="bg-orange-50 border-l-4 border-l-orange-400 rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {firstBillMetrics.map((metric, index) => (
              <QuickStatsCard
                key={index}
                title={metric.label}
                count={metric.value as number}
                trendSummary={metric.subtext}
                icon={metric.icon}
              />
            ))}
          </div>
          <div className="space-y-3">
            {/* Toggle Card */}
            <div className="flex items-center justify-between bg-white rounded-lg p-4 border">
              <div>
                <p className="font-medium">
                  Include deposit and installation charges in the first bills
                </p>
                <p className="text-sm text-gray-600">
                  Automatically add one-time fees to new consumer bills
                </p>
              </div>

              <Switch
                checked={includeFirstBillCharges}
                onCheckedChange={setIncludeFirstBillCharges}
              />
            </div>

            {!includeFirstBillCharges && (
              <div className="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
                <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />

                <div>
                  <p className="text-sm font-medium text-red-700">
                    Revenue Leakage Warning
                  </p>
                  <p className="text-sm text-red-600">
                    Deposit and installation charges should be included in the
                    first bills to prevent revenue leakage. Excluding these
                    charges may result in uncollected fees and financial
                    discrepancies.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Consumer Account Type Distribution */}
      <div className="mb-6">
        <SectionHeader
          title="Consumer Account Type Distribution"
          description="Breakdown of consumers by account type for the selected billing cycle."
        />
        <div className="bg-white mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {accountTypeMetrics.map((metric, index) => (
              <QuickStatsCard
                key={index}
                title={metric.title}
                count={metric.count}
                displayValue={metric.displayValue}
                trend={metric.trend}
                trendDurationDisplay={metric.trendDurationDisplay}
                icon={metric.icon}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Category Wise Consumer Distribution */}
      <div className="mb-6">
        <SectionHeader
          title="Category Wise Consumer Distribution"
          description="Breakdown of consumers by their assigned category"
        />
        <div className="bg-white ">
          <div className="space-y-3 rounded-lg border border-gray-200 p-4">
            {consumerDashboardData?.categoryWiseDistribution?.length ? (
              consumerDashboardData.categoryWiseDistribution.map(
                (category, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>{category.category ?? "NA"}</span>
                      <span className="text-gray-600">
                        {category.count} consumers ({category.percentage}%)
                      </span>
                    </div>

                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${category.percentage}%` }}
                      />
                    </div>
                  </div>
                ),
              )
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                No category-wise distribution data available
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Cycle Comparison */}
      <div className="mb-6">
        <SectionHeader
          title="Cycle Comparison"
          description="Comparison of consumers across cycles."
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 rounded-lg">
          {consumerStats.map(({ label, value, bg, border, icon }) => (
            <QuickStatsCard
              key={label}
              title={label}
              count={value}
              icon={icon}
            />
          ))}
        </div>
      </div>

      {/* Consumer List (Collapsible) */}
      <div className="bg-white rounded-lg border">
        <Button
          onClick={() => setShowConsumerList(!showConsumerList)}
          className="w-full flex justify-between items-center p-4 hover:bg-gray-50 transition-colors p-6"
          variant="outline"
        >
          <div className="text-left p-4 mt-2 mb-2">
            <h3 className="font-semibold">Consumer List</h3>
            <p className="text-sm text-gray-600">
              Complete list of consumers in the current cycle
            </p>
          </div>
          {showConsumerList ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <span className="flex items-center gap-1 cursor-pointer">
              <p className="text-sm text-gray-600">click to view list </p>
              <ChevronDown className="w-5 h-5 text-gray-400" />
            </span>
          )}
        </Button>

        {showConsumerList && (
          <div className="p-4 border-t">
            <GenericTable
              data={consumerList?.result || []}
              columns={columns}
              rowKey="id"
              loading={false}
              emptyMessage="No consumers found"
              pagination={{
                currentPage: currentPage,
                totalPages: Math.ceil(consumerList?.count / PAGE_SIZE),
                totalItems: consumerList?.count,
                itemsPerPage: PAGE_SIZE,
                startItem: (currentPage - 1) * PAGE_SIZE + 1,
                endItem: Math.min(currentPage * PAGE_SIZE, consumerList?.count),
              }}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsumerTab;
